// options for elastic dates
// https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-range-query.html
import { endOfDay, isSameDay, subDays } from 'date-fns';
import { isNumber, isString } from 'lodash-es';

import relativeTimeToDate from './relative_time_to_date';

export type StrictDateRange = {
  min: string;
  max: string;
};

export type CalendarDropdownOption = {
  label: string;
  value: StrictDateRange | string;
};

export const calendarOptionsPresets: CalendarDropdownOption[] = [
  { label: 'Today', value: { min: 'now/d', max: 'now' } },
  { label: 'Yesterday', value: { min: 'now-1d/d', max: 'now/d' } },
  { label: 'Last week', value: { min: 'now-1w/w', max: 'now/w' } },
  { label: 'Last month', value: { min: 'now-1M/M', max: 'now/M' } },
  { label: 'Last year', value: { min: 'now-1y/y', max: 'now/y' } },
];

export const calendarOptionsDropdown: CalendarDropdownOption[] = [
  { label: 'days to today', value: { min: 'now-%d', max: 'now' } },
  { label: 'weeks to today', value: { min: 'now-%w', max: 'now' } },
  { label: 'months to today', value: { min: 'now-%M', max: 'now' } },
  { label: 'years to today', value: { min: 'now-%y', max: 'now' } },

  { label: 'days from today', value: { max: 'now+%d', min: 'now' } },
  { label: 'weeks from today', value: { max: 'now+%w', min: 'now' } },
  { label: 'months from today', value: { max: 'now+%M', min: 'now' } },
  { label: 'years from today', value: { max: 'now+%y', min: 'now' } },
];

export type DateRangeValue = string | Date | number;
export type DateRange = {
  min?: DateRangeValue;
  max?: DateRangeValue;
};

export const relativeToDate = (
  val: Date | DateRange | string,
): DateRange | Date | string | number => {
  let value: DateRange = {};
  try {
    if (isString(val)) {
      return relativeTimeToDate(val);
    }
    if ('min' in val && val.min && val.max) {
      const minDate = relativeTimeToDate(val.min);
      const maxDate = relativeTimeToDate(val.max);
      // in the calendar max should be represented as the end of the day before
      // for relative dates not including today
      const sameDay = isSameDay(minDate, maxDate);
      const max =
        sameDay || val.max === 'now' || val.min === 'now'
          ? maxDate
          : endOfDay(subDays(maxDate, 1));

      value = { min: minDate, max };
    }
  } catch (_err) {
    return {};
  }

  return value;
};

type SelectValue = DateRange | string;

// replace % with a number wherever it is in the select option
export const formatSelectValue = (
  select: SelectValue,
  value: number,
): SelectValue =>
  JSON.parse(JSON.stringify(select).replace(/%/g, String(value)));

export type FormatSelectValue = typeof formatSelectValue;

export const formatNumberValue = (
  value: number,
  select: SelectValue,
): SelectValue => formatSelectValue(select, value);

export type OptionAndNumber = { number?: number; option?: string };

export const matchSelectValue = (
  currentValue: string | number | undefined | Date,
  option: string | number | undefined | null | Date,
  index: number,
): OptionAndNumber | null => {
  if (option === 'now') {
    return null;
  }
  if (!isString(option)) {
    return null;
  }
  if (!isString(currentValue)) {
    return null;
  }
  if (!isNumber(index)) {
    return null;
  }
  const numberMarch = currentValue.match(/\d+/);
  if (numberMarch) {
    const number = Number(numberMarch[0]);
    if (option.replace(/%/g, String(number)) === currentValue) {
      return { number, option: String(index) };
    }
  }
  return null;
};

export type MatchSelectValue = typeof matchSelectValue;
